/**
 * useRegistrationFlow — FW-129, 11/12/13-kayit-*'nin UC adimli (start ->
 * verify-otp -> complete) durum makinesi. `useInviteRegistrationFlow.ts`'in
 * davet-BAGIMSIZ ikizi (brief madde 3): `invitationToken` YOK, tenant YOK.
 *
 * TASARIM FARKI (bilerek): `useInviteRegistrationFlow` TEK bir bilesende
 * (`InviteRegisterView.vue`) `v-show` ile adim degistirir — o yuzden adim
 * verisini SIRADAN `ref()`lerde tutabilir (bilesen hic UNMOUNT olmaz). Bu
 * kayit akisi ise brief'in istedigi gibi UC AYRI ROTADIR (`/register`,
 * `/register/otp`, `/register/password` — bkz. routes.ts), TIPKI giris
 * akisinin dort karesi (email/password/pick/done) gibi: her rota gecisinde
 * vue-router bilesen agacini UNMOUNT/MOUNT eder. Adimlar arasi veri
 * (girilen ad/soyad/e-posta, kayit jetonu, kalan OTP deneme sayisi) bu
 * yuzden route-local `ref()`lerde YASAYAMAZ — `useAuthFlowState.ts`nin
 * AYNI gerekcesiyle, MODUL kapsamli tekil bir `reactive()` nesnede tutulur.
 */
import { reactive, readonly, type DeepReadonly } from 'vue'
import { useI18n } from '@fusapp-work/shell'
import { ApiError } from '@fusapp-work/api'
import { useRegistrationStart } from './useRegistrationStart'
import { useRegistrationVerifyOtp } from './useRegistrationVerifyOtp'
import { useRegistrationComplete } from './useRegistrationComplete'
import { isValidEmail, isValidRegistrationPassword } from '../domain/authValidation'
import { parseRegistrationStartResponse, parseRegistrationVerificationResponse } from '../domain/registrationContract'

/** 12-kayit-otp ipucu'su: "3 deneme hakkı". */
export const REGISTRATION_MAX_OTP_ATTEMPTS = 3

interface RegistrationFlowStateData {
  firstName: string
  lastName: string
  email: string
  registrationToken: string | null
  /** Absolute server deadlines survive route remounts without renewal. */
  otpExpiresAt: number | null
  tokenExpiresAt: number | null
  otpExpired: boolean
  remainingOtpAttempts: number
  otpVerified: boolean
}

function createInitialState(): RegistrationFlowStateData {
  return {
    firstName: '',
    lastName: '',
    email: '',
    registrationToken: null,
    otpExpiresAt: null,
    tokenExpiresAt: null,
    otpExpired: false,
    remainingOtpAttempts: REGISTRATION_MAX_OTP_ATTEMPTS,
    otpVerified: false,
  }
}

const state = reactive<RegistrationFlowStateData>(createInitialState())
// Starting/resending may rotate the server token before delivery succeeds.
// Invalidate older responses immediately, including responses from an unmounted view.
let stateRevision = 0
let verificationRevision = 0

/**
 * routes.ts'in guard'lari ICIN — SADECE OKUMA. `useRegistrationFlow()`nun
 * KENDISI cagrilmaz: o `useI18n()`/`useMutation()` (Vue Query inject)
 * TETIKLER, bunlar bir router guard baglaminda (bilesen `setup()` DISINDA)
 * GUVENLI DEGILDIR — `useAuthFlowState.ts`nin route guard'lardan dogrudan
 * cagrilabilir SAF kalmasinin AYNI gerekcesi. Bu erisimci hicbir composition
 * API cagirmaz, yalniz MODUL singleton'ini okur.
 */
export function registrationFlowSnapshot(): DeepReadonly<RegistrationFlowStateData> {
  return readonly(state)
}

/**
 * `registrationFlowSnapshot()`in YAZMA ikizi — AYNI gerekce: testlerin
 * `beforeEach`i (`useAuthFlowState().reset()`nin bu composable'daki KARSILIGI)
 * bir bilesen `setup()`u DISINDA cagrilir, `useRegistrationFlow()`nun
 * KENDISI orada GUVENLI DEGILDIR. `flow.reset()` (composable donusu) de
 * BUNU cagirir — TEK bir gercek kaynak.
 */
export function resetRegistrationFlowState(): void {
  stateRevision += 1
  Object.assign(state, createInitialState())
}

export interface RegistrationActionResult {
  readonly success: boolean
  readonly errorMessage: string | null
}

export interface RegistrationCompleteResult extends RegistrationActionResult {
  readonly postRegisterRedirect: string | null
}

export interface UseRegistrationFlow {
  readonly state: DeepReadonly<RegistrationFlowStateData>
  readonly isStartPending: { readonly value: boolean }
  readonly isVerifyPending: { readonly value: boolean }
  readonly isCompletePending: { readonly value: boolean }
  start(firstName: string, lastName: string, email: string): Promise<RegistrationActionResult>
  resendOtp(): Promise<RegistrationActionResult>
  verifyOtp(code: string): Promise<RegistrationActionResult>
  complete(password: string): Promise<RegistrationCompleteResult>
  reset(): void
}

export function useRegistrationFlow(): UseRegistrationFlow {
  const { t } = useI18n()
  const registrationStart = useRegistrationStart()
  const registrationVerifyOtp = useRegistrationVerifyOtp()
  const registrationComplete = useRegistrationComplete()

  function resolveErrorMessage(error: unknown): string {
    if (error instanceof ApiError) {
      if (error.status === 409) return t('auth.register.errors.emailExists')
      if (error.status === 400) return t('auth.register.errors.invalid')
    }
    return t('auth.register.errors.generic')
  }

  async function start(firstName: string, lastName: string, email: string): Promise<RegistrationActionResult> {
    const trimmedFirst = firstName.trim()
    const trimmedLast = lastName.trim()
    const trimmedEmail = email.trim()
    if (trimmedFirst === '' || trimmedLast === '') {
      return { success: false, errorMessage: t('auth.register.errors.required') }
    }
    if (!isValidEmail(trimmedEmail)) {
      return { success: false, errorMessage: t('auth.register.errors.invalidEmail') }
    }
    const revision = ++stateRevision
    state.otpVerified = false
    try {
      const response = await registrationStart.mutateAsync({ firstName: trimmedFirst, lastName: trimmedLast, email: trimmedEmail })
      if (revision !== stateRevision) return { success: false, errorMessage: null }
      const result = parseRegistrationStartResponse(response, Date.now())
      if (result === null) return { success: false, errorMessage: t('auth.register.errors.generic') }
      Object.assign(state, {
        firstName: trimmedFirst,
        lastName: trimmedLast,
        email: trimmedEmail,
        ...result,
        remainingOtpAttempts: REGISTRATION_MAX_OTP_ATTEMPTS,
        otpVerified: false,
        otpExpired: false,
      })
      return { success: true, errorMessage: null }
    } catch (error) {
      if (revision !== stateRevision) return { success: false, errorMessage: null }
      return { success: false, errorMessage: resolveErrorMessage(error) }
    }
  }

  /** Cosmos resends by starting again with the same identity, not a separate endpoint. */
  async function resendOtp(): Promise<RegistrationActionResult> {
    if (state.email === '') return { success: false, errorMessage: t('auth.register.errors.generic') }
    return start(state.firstName, state.lastName, state.email)
  }

  function abandonRegistration(): void {
    stateRevision += 1
    state.registrationToken = null
    state.otpExpiresAt = null
    state.tokenExpiresAt = null
    state.otpVerified = false
  }

  function handleOtpError(error: unknown): RegistrationActionResult {
    if (error instanceof ApiError) {
      if (error.code === 'IDENTITY_REGISTRATION_TOKEN_INVALID') {
        abandonRegistration()
        return { success: false, errorMessage: t('auth.register.errors.tokenInvalid') }
      }
      if (error.code === 'IDENTITY_REGISTRATION_OTP_ATTEMPTS_EXCEEDED') {
        state.remainingOtpAttempts = 0
        abandonRegistration()
        return { success: false, errorMessage: t('auth.register.errors.otpAttemptsExhausted') }
      }
      if (error.code === 'IDENTITY_REGISTRATION_OTP_EXPIRED') {
        state.otpExpired = true
        return { success: false, errorMessage: t('auth.register.errors.otpExpired') }
      }
      if (error.code === 'IDENTITY_REGISTRATION_OTP_INVALID' || error.status === 400) {
        return { success: false, errorMessage: t('auth.register.errors.otpInvalid') }
      }
    }
    return { success: false, errorMessage: resolveErrorMessage(error) }
  }

  async function verifyOtp(code: string): Promise<RegistrationActionResult> {
    const trimmedCode = code.trim()
    const token = state.registrationToken
    if (trimmedCode === '' || token === null) {
      return { success: false, errorMessage: t('auth.register.errors.otpRequired') }
    }
    const revision = stateRevision
    const verification = ++verificationRevision
    state.otpVerified = false
    const isCurrent = (): boolean => revision === stateRevision && verification === verificationRevision && token === state.registrationToken
    try {
      const response = await registrationVerifyOtp.mutateAsync({ registrationToken: token, code: trimmedCode })
      if (!isCurrent()) return { success: false, errorMessage: null }
      const result = parseRegistrationVerificationResponse(response)
      if (result === null) return { success: false, errorMessage: t('auth.register.errors.generic') }
      state.remainingOtpAttempts = result.remainingAttempts
      if (!result.verified) {
        if (result.remainingAttempts === 0) {
          abandonRegistration()
          return { success: false, errorMessage: t('auth.register.errors.otpAttemptsExhausted') }
        }
        return { success: false, errorMessage: t('auth.register.errors.otpInvalid') }
      }
      state.otpVerified = true
      return { success: true, errorMessage: null }
    } catch (error) {
      if (!isCurrent()) return { success: false, errorMessage: null }
      return handleOtpError(error)
    }
  }

  async function complete(password: string): Promise<RegistrationCompleteResult> {
    if (!isValidRegistrationPassword(password) || state.registrationToken === null) {
      return { success: false, errorMessage: t('auth.register.errors.passwordInvalid'), postRegisterRedirect: null }
    }
    try {
      const result = await registrationComplete.mutateAsync({
        registrationToken: state.registrationToken,
        password,
      })
      return { success: true, errorMessage: null, postRegisterRedirect: result.postRegisterRedirect }
    } catch (error) {
      return { success: false, errorMessage: resolveErrorMessage(error), postRegisterRedirect: null }
    }
  }

  function reset(): void {
    resetRegistrationFlowState()
  }

  return {
    state: readonly(state),
    isStartPending: registrationStart.isPending,
    isVerifyPending: registrationVerifyOtp.isPending,
    isCompletePending: registrationComplete.isPending,
    start,
    resendOtp,
    verifyOtp,
    complete,
    reset,
  }
}
